import axios from 'axios'
import { ref, computed } from 'vue'
import { usePopup } from '@/composables/ui/usePopup'
import useNotification from '@/composables/useNotification.js'
import useFormattedData from '@/composables/useFormattedData'

const URLS = {
    xhr: {
        fetch: {
            checkAuth: '/api/check-authorization',
            userPaymentMethods: '/api/profile/stripe/get-payment-methods'
        },
        mutate: {
            addCard: '/api/profile/event-add-payment-method',
            defaultCard: '/api/profile/checkout-payment-method'
        }
    }
}

const userPaymentMethods = ref([])
const showAddCardForm = ref(false)
const cardFormValues = ref({
    cardholder: '',
    exp_date: '',
    card_number: '',
    cvc: ''
})
const isAuthed = ref(false)
const load = ref(false)

export function usePaymentMethods() {
    const { notification } = useNotification()
    const { formatCreditCardNumInput } = useFormattedData()
    const { popupOpen: dropdownOpen } = usePopup()

    const favPayment = computed(() => userPaymentMethods.value.find((payment) => !!payment.favourite))
    const otherPayments = computed(() => userPaymentMethods.value.filter((payment) => payment.favourite !== 1))
    const duplicateCard = computed(() =>
        userPaymentMethods.value.some((card) => {
            const expDate = cardFormValues.value.exp_date.split('/')
            const month = expDate[0]
            const year = expDate[1]
            return (
                card.last_four === cardFormValues.value.card_number.slice(-4) &&
                `${card.exp_month}/${card.exp_year}` === `${month}/20${year}`
            )
        })
    )

    ;(async () => {
        if (!isAuthed.value) await _checkUserAuth()
        if (!userPaymentMethods.value.length && isAuthed.value) await _fetchPaymentMethods()
    })()

    function formatCreditCardInput(event) {
        const formattedCC = formatCreditCardNumInput(event, cardFormValues.value.card_number)

        if (!formattedCC) return true
        cardFormValues.value.card_number = formattedCC
    }

    async function selectCard(id) {
        try {
            load.value = true
            await axios.get(`${URLS.xhr.mutate.defaultCard}/${id}`)
            _selectCard(id)
        } catch (e) {
            notification('error', e)
        } finally {
            load.value = false
        }
    }
    async function addPaymentMethod(v) {
        if (!v.cardFormValues.$invalid && !duplicateCard.value) {
            try {
                load.value = true
                const data = _prepAddCard()
                const response = await axios.post('/profile/event-add-payment-method', data)
                _addNewCard(response)
                _clearCardForm(v)
                showAddCardForm.value = false
                notification('success', response.data.message)
            } catch (error) {
                notification('error', error)
            } finally {
                load.value = false
            }
        }
    }
    function _prepAddCard() {
        const name = cardFormValues.value.cardholder.split(' ')
        const firstName = name[0]
        const lastName = name[1]
        const cardNumber = cardFormValues.value.card_number.replace(/\s/g, '')

        return {
            first_name: firstName,
            last_name: lastName,
            card_number: cardNumber,
            exp_date: cardFormValues.value.exp_date,
            cvc: cardFormValues.value.cvc
        }
    }
    function _addNewCard(response) {
        const exp_month = cardFormValues.value.exp_date.split('/')[0]
        const exp_year = ` 20${cardFormValues.value.exp_date.split('/')[1]}`
        const cardData = {
            cardholder_name: response.data.data.cardholder_name,
            last_four: response.data.data.last_four,
            brand: response.data.data.brand,
            exp_year: exp_year,
            exp_month: exp_month,
            card_stripe_id: response.data.data.card,
            id: response.data.data.id,
            favourite: response.data.data.favourite
        }
        userPaymentMethods.value.push(cardData)

        return selectCard(cardData.id)
    }
    function _clearCardForm(v) {
        cardFormValues.value.cardholder = ''
        cardFormValues.value.exp_date = ''
        cardFormValues.value.card_number = ''
        cardFormValues.value.cvc = ''

        v.$reset()
    }
    function _selectCard(id) {
        const card = userPaymentMethods.value.find((card) => card.id === id)
        const otherCards = userPaymentMethods.value.filter((card) => card.id !== id)

        card.favourite = 1
        otherCards.forEach((card) => (card.favourite = 0))
        dropdownOpen.value = false
    }

    async function _checkUserAuth() {
        try {
            const resp = await axios.get(URLS.xhr.fetch.checkAuth)

            if (resp?.data?.isAuthorized) {
                isAuthed.value = true
            }
        } catch {
            isAuthed.value = false
        }
    }
    async function _fetchPaymentMethods() {
        try {
            load.value = true
            const response = await axios.get(URLS.xhr.fetch.userPaymentMethods)

            userPaymentMethods.value = response.data.data
        } catch (error) {
            notification('error', error)
        } finally {
            load.value = false
        }
    }

    return {
        load,
        userPaymentMethods,
        favPayment,
        otherPayments,
        dropdownOpen,
        showAddCardForm,
        cardFormValues,
        duplicateCard,
        selectCard,
        formatCreditCardInput,
        addPaymentMethod
    }
}
